/* eslint-disable */
import { useState } from "react";
import { C } from "../shared/globals.js";

// ─── helpers ──────────────────────────────────────────────────────────────
const fmtWeekLabel = weekKey => {
  const d = new Date(weekKey);
  const mo = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"][d.getMonth()];
  return `${String(d.getDate()).padStart(2, "0")} ${mo}`;
};

// ─── Main export ──────────────────────────────────────────────────────────
// monthlyData : [{ month:"YYYY-MM", dollars, shares }]
// weeklyData  : [{ week:"YYYY-MM-DD", dollars, shares }]
export default function SharesPerPeriod({ monthlyData = [], weeklyData = [], isMobile = false }) {
  const [sharesSort, setSharesSort] = useState({ key: "date", dir: "asc" });
  const [weekSort,   setWeekSort]   = useState({ key: "date", dir: "asc" });

  if (!monthlyData.length && !weeklyData.length) return null;

  const sortedMonths = [...monthlyData].sort((a, b) => {
    const dir = sharesSort.dir === "asc" ? 1 : -1;
    if (sharesSort.key === "date")   return a.month.localeCompare(b.month) * dir;
    if (sharesSort.key === "shares") return (b.shares - a.shares) * dir;
    const aAvg = a.shares > 0 ? a.dollars / a.shares : 0;
    const bAvg = b.shares > 0 ? b.dollars / b.shares : 0;
    return (bAvg - aAvg) * dir;
  });
  const maxSharesVal = Math.max(...monthlyData.map(x => x.shares), 1);

  const sortedWeeks = [...weeklyData].sort((a, b) => {
    const dir = weekSort.dir === "asc" ? 1 : -1;
    if (weekSort.key === "date")   return a.week.localeCompare(b.week) * dir;
    if (weekSort.key === "shares") return (b.shares - a.shares) * dir;
    const aAvg = a.shares > 0 ? a.dollars / a.shares : 0;
    const bAvg = b.shares > 0 ? b.dollars / b.shares : 0;
    return (bAvg - aAvg) * dir;
  });
  const maxSharesWeek = Math.max(...weeklyData.map(x => x.shares), 1);

  const SortBtns = ({ active, setSort, accentColor }) => (
    <div style={{ display: "flex", gap: 4 }}>
      {[{ key: "date", label: "DATE" }, { key: "shares", label: "SHARES" }, { key: "avg", label: "AVG" }].map(({ key, label }) => {
        const isActive = active.key === key;
        return (
          <button
            key={key}
            onClick={() => setSort(s => s.key === key
              ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
              : { key, dir: "asc" }
            )}
            style={{
              background: isActive ? `${accentColor}22` : "transparent",
              border: `1px solid ${isActive ? accentColor : "var(--border)"}`,
              borderRadius: 5,
              padding: "3px 7px",
              cursor: "pointer",
              fontFamily: "Montserrat,sans-serif",
              fontSize: 7,
              fontWeight: 700,
              color: isActive ? accentColor : "var(--text4)",
              transition: "all 0.15s",
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? 20 : 24 }}>

      {/* ── PER MONTH ── */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ fontFamily: "Montserrat,sans-serif", fontSize: 8, fontWeight: 700, letterSpacing: "0.25em", color: "var(--text4)" }}>
            PER MONTH
          </div>
          <SortBtns active={sharesSort} setSort={setSharesSort} accentColor="#a78bfa" />
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {sortedMonths.map(m => {
            const [y, mo] = m.month.split("-");
            const names = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
            const monthName = names[parseInt(mo) - 1];
            const intensity = maxSharesVal > 0 ? m.shares / maxSharesVal : 0;
            const bg = `hsla(270,35%,${20 + intensity * 18}%,1)`;
            return (
              <div
                key={m.month}
                style={{
                  background: bg,
                  borderRadius: 10,
                  padding: isMobile ? "6px 8px" : "10px 12px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  width: isMobile ? 52 : 76,
                  flexShrink: 0,
                  transition: "transform 0.15s",
                }}
                onMouseEnter={e => e.currentTarget.style.transform = "scale(1.05)"}
                onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
              >
                <div style={{ fontFamily: "Montserrat,sans-serif", fontSize: isMobile ? 6 : 7, fontWeight: 700, letterSpacing: "0.1em", color: "hsl(270,30%,70%)" }}>
                  {monthName} '{y.slice(2)}
                </div>
                <div style={{ fontFamily: "Montserrat,sans-serif", fontSize: isMobile ? 13 : 16, fontWeight: 900, color: "rgba(255,255,255,0.5)", lineHeight: 1 }}>
                  {m.shares.toFixed(isMobile ? 1 : 2)}
                </div>
                <div style={{ fontFamily: "Montserrat,sans-serif", fontSize: 6, fontWeight: 700, letterSpacing: "0.1em", color: "hsl(270,30%,70%)" }}>
                  SH
                </div>
                {!isMobile && (
                  <div style={{ fontFamily: "Montserrat,sans-serif", fontSize: 8, color: "rgba(255,255,255,0.3)" }}>
                    ${m.shares > 0 ? (m.dollars / m.shares).toFixed(0) : "—"}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Divider */}
      <div style={{ width: 1, background: "var(--border)", flexShrink: 0, display: isMobile ? "none" : "block" }} />

      {/* ── PER WEEK ── */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ fontFamily: "Montserrat,sans-serif", fontSize: 8, fontWeight: 700, letterSpacing: "0.25em", color: "var(--text4)" }}>
            PER WEEK
          </div>
          <SortBtns active={weekSort} setSort={setWeekSort} accentColor="#60a5fa" />
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {sortedWeeks.map(w => {
            const intensity = maxSharesWeek > 0 ? w.shares / maxSharesWeek : 0;
            const bg = `hsla(213,85%,${16 + intensity * 20}%,1)`;
            const wAvg = w.shares > 0 ? w.dollars / w.shares : 0;
            return (
              <div
                key={w.week}
                style={{
                  background: bg,
                  borderRadius: 10,
                  padding: isMobile ? "6px 8px" : "10px 12px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  width: isMobile ? 48 : 72,
                  flexShrink: 0,
                  transition: "transform 0.15s",
                }}
                onMouseEnter={e => e.currentTarget.style.transform = "scale(1.05)"}
                onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
              >
                <div style={{ fontFamily: "Montserrat,sans-serif", fontSize: isMobile ? 6 : 7, fontWeight: 700, letterSpacing: "0.05em", color: "rgba(255,255,255,0.55)", textAlign: "center" }}>
                  {fmtWeekLabel(w.week)}
                </div>
                <div style={{ fontFamily: "Montserrat,sans-serif", fontSize: isMobile ? 12 : 14, fontWeight: 900, color: "rgba(255,255,255,0.5)", lineHeight: 1 }}>
                  {w.shares.toFixed(isMobile ? 1 : 2)}
                </div>
                <div style={{ fontFamily: "Montserrat,sans-serif", fontSize: 6, fontWeight: 700, letterSpacing: "0.1em", color: "rgba(255,255,255,0.4)" }}>
                  SH
                </div>
                {!isMobile && (
                  <div style={{ fontFamily: "Montserrat,sans-serif", fontSize: 8, color: "rgba(255,255,255,0.3)" }}>
                    ${wAvg > 0 ? wAvg.toFixed(0) : "—"}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
}
